import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight, Compass, ShieldCheck, Target, Users } from 'lucide-react';
import { SERVICES, PRINCIPLES, SIGNALS, INSIGHTS, STATS, IMAGES } from '../mock/mock';
import usePageMeta from '../hooks/usePageMeta';

const Section = ({ children, className = '' }) => (
  <section className={`max-w-[1400px] mx-auto px-6 lg:px-10 ${className}`}>{children}</section>
);

export default function Home() {
  usePageMeta({
    path: '/',
    description: 'Waypoint Transformation places seasoned CIOs, CTOs, engineering and technical leaders, program leaders, and senior engineers inside organizations that need executive-grade direction — without the full-time commitment.',
  });
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#E8EEF5] blur-3xl opacity-70" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-[#DCE6F0] blur-3xl opacity-60" />
        </div>
        <Section className="relative pt-24 pb-24 lg:pt-32 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8 fade-up">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-8 h-px bg-[#0F2A44]" />
                <span className="text-[11px] uppercase tracking-[0.24em] text-[#0F2A44] font-semibold">Fractional Technology Leadership</span>
              </div>
              <h1 className="font-serif text-[46px] sm:text-[64px] lg:text-[84px] leading-[0.98] tracking-[-0.02em] text-[#0F2A44] text-balance">
                Technology leadership,<br/>
                <span className="italic text-[#3E6A94]">on your terms.</span>
              </h1>
              <p className="mt-8 text-[18px] lg:text-[20px] leading-relaxed text-slate-600 max-w-2xl">
                Waypoint Transformation places seasoned CIOs, CTOs, engineering and technical leaders, program leaders, and senior engineers inside organizations that need executive-grade direction — without the full-time commitment. Grounded in collaboration, trust, integrity, and execution.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" className="group inline-flex items-center gap-2 px-7 py-4 bg-[#0F2A44] text-white text-[14px] font-medium tracking-tight hover:bg-[#1E3A5F] transition-colors">
                  Start a conversation
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
                <Link to="/services" className="inline-flex items-center gap-2 px-7 py-4 border border-[#0F2A44]/20 text-[#0F2A44] text-[14px] font-medium hover:border-[#0F2A44] hover:bg-[#0F2A44]/[0.03] transition-colors">
                  Explore services
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4 lg:pl-8">
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-6 lg:gap-6">
                {[
                  { icon: Users, label: 'Collaboration' },
                  { icon: ShieldCheck, label: 'Trust' },
                  { icon: Target, label: 'Execution' },
                  { icon: Compass, label: 'Integrity' },
                ].map((v) => (
                  <div key={v.label} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-[#0F2A44]/5 flex items-center justify-center">
                      <v.icon className="w-4 h-4 text-[#0F2A44]" />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Brand pillar</div>
                      <div className="font-serif text-lg text-[#0F2A44]">{v.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Hero image band */}
        <Section className="pb-20">
          <div className="relative aspect-[21/9] w-full overflow-hidden rounded-sm">
            <img src={IMAGES.heroBoardroom} alt="Executive strategy" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2A44]/60 via-[#0F2A44]/10 to-transparent" />
            <div className="absolute bottom-8 left-8 lg:bottom-12 lg:left-12 text-white max-w-xl">
              <div className="text-[11px] uppercase tracking-[0.2em] opacity-80">Chicago · Serving clients on 6 continents</div>
              <div className="mt-3 font-serif text-2xl lg:text-3xl leading-tight">Experienced leadership, without the full-time overhead.</div>
            </div>
          </div>
        </Section>
      </section>

      {/* Stats */}
      <section className="bg-[#F5F7FA] border-y border-slate-200">
        <Section className="py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            {STATS.map((s) => (
              <div key={s.label}>
                <div className="font-serif text-4xl lg:text-5xl text-[#0F2A44] tracking-tight">{s.value}</div>
                <div className="mt-2 text-sm text-slate-600 max-w-[240px]">{s.label}</div>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* Signals */}
      <Section className="py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">The moment to act</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-[#0F2A44] tracking-tight">When technical leadership becomes the constraint.</h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-md">Growth exposes decisions that can’t be deferred, and the cost of getting them wrong compounds quickly. These are the signals we see — and where we come in.</p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {SIGNALS.map((s) => (
              <div key={s.title} className="bg-white p-8 hover:bg-[#F5F7FA] transition-colors">
                <div className="font-serif text-xl text-[#0F2A44] mb-3">{s.title}</div>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Services */}
      <section className="bg-[#0F2A44] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <Section className="relative py-24 lg:py-32">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.24em] text-[#7DB4D8] mb-4">What we do</div>
              <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] tracking-tight">Senior-level leadership, engaged the way your business needs it.</h2>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 text-white text-sm border-b border-white/40 pb-1 hover:border-white transition-colors">
              All services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {SERVICES.slice(0, 6).map((s) => (
              <Link key={s.id} to={`/services#${s.id}`} className="group bg-[#0F2A44] p-8 lg:p-10 hover:bg-[#132F4C] transition-colors">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#7DB4D8] mb-4">{s.kicker}</div>
                <h3 className="font-serif text-2xl text-white mb-4">{s.title}</h3>
                <p className="text-slate-300 text-[15px] leading-relaxed">{s.tagline}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-sm text-white opacity-80 group-hover:opacity-100 group-hover:gap-3 transition-all">
                  Learn more <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </Section>
      </section>

      {/* Approach */}
      <Section className="py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
              <img src={IMAGES.ai2} alt="AI transformation" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0F2A44]/40" />
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Our approach</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-[#0F2A44] tracking-tight">Five principles that make transformations stick.</h2>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-2xl">We map your desired outcomes to your people, processes, and technology environment — then underpin the model with five operating principles that keep the program disciplined.</p>
            <div className="mt-10 divide-y divide-slate-200 border-t border-slate-200">
              {PRINCIPLES.map((p, i) => (
                <div key={p.name} className="py-6 grid grid-cols-12 gap-6 group">
                  <div className="col-span-1 text-slate-400 text-sm pt-1">{String(i + 1).padStart(2, '0')}</div>
                  <div className="col-span-11 sm:col-span-4 font-serif text-xl text-[#0F2A44] group-hover:text-[#3E6A94] transition-colors">{p.name}</div>
                  <div className="col-span-12 sm:col-span-7 text-slate-600 text-[15px] leading-relaxed">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Global */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.globe1} alt="Global network" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0F2A44]/80" />
        </div>
        <Section className="relative py-24 lg:py-32 text-white">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#7DB4D8] mb-6">Global transformation</div>
            <h2 className="font-serif text-4xl lg:text-6xl leading-[1.02] tracking-tight">Delivering large-scale programs on every continent.</h2>
            <p className="mt-8 text-lg text-slate-200 leading-relaxed max-w-2xl">ERP, Financials, HR, Payroll, Workforce Management, single sign-on — our leaders bring the operating rigor complex programs demand, and the calm judgment stakeholders expect.</p>
            <Link to="/services#global-transformation" className="inline-flex items-center gap-2 mt-10 px-7 py-4 bg-white text-[#0F2A44] text-sm font-medium hover:bg-slate-100 transition-colors">
              Explore transformation <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Section>
      </section>

      {/* Insights */}
      <Section className="py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Insights</div>
            <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-[#0F2A44] tracking-tight max-w-2xl">Perspectives from the field.</h2>
          </div>
          <Link to="/insights" className="inline-flex items-center gap-2 text-[#0F2A44] text-sm border-b border-[#0F2A44]/40 pb-1 hover:border-[#0F2A44] transition-colors">
            All insights <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {INSIGHTS.map((post) => (
            <Link key={post.id} to={`/insights/${post.id}`} className="group block">
              <div className="relative aspect-[4/3] overflow-hidden rounded-sm mb-5">
                <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">{post.category}</div>
              <h3 className="font-serif text-xl text-[#0F2A44] leading-snug mb-3 group-hover:text-[#3E6A94] transition-colors">{post.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{post.excerpt}</p>
              <div className="mt-4 text-xs text-slate-500">{post.date} · {post.readTime}</div>
            </Link>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-[#F5F7FA] border-t border-slate-200">
        <Section className="py-24 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-[#0F2A44] tracking-tight">Ready to move with confidence?</h2>
              <p className="mt-5 text-lg text-slate-600 max-w-2xl">Tell us where you are and where you’re trying to go. We’ll bring the leadership — and the discipline — to help you get there.</p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0F2A44] text-white text-sm font-medium hover:bg-[#1E3A5F] transition-colors">
                Start a conversation <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Section>
      </section>
    </div>
  );
}
